/**
 * RepaymentShapeCard — renders the `get_repayment_shape` tool result (T-39).
 *
 * Chat adaptation of the dashboard StructureSummaryCard (NOT a reuse). The hero
 * switches on the engine's `hero_kind`:
 *   payment   → the FIRST period's payment + cadence + first-payment date
 *   balloon   → the deterministic balloon amount at maturity
 *   irregular → a sculpted/cash-sweep statement (no number)
 *   revolving → a revolving-facility statement (no fixed payment)
 *   none      → shape-only partial (rate/type absent) — disclosure carries it
 *
 * ENGINE CONTRACT (post-review caveat 2026-07-06): derive_repayment_shape returns
 * the first period's payment + the first-payment date — NOT a today-relative
 * "instalment N of M / next payment due <date>" counter. This card renders ONLY
 * what the dataclass provides; it never fabricates a live countdown.
 *
 * Renders nothing (prose carries) when `available === false` — no analyzed term
 * sheet or a `pending` tier (principal/term absent). "A card renders OR prose
 * carries, never both." (Banker/CPO edge gate 2026-07-06.)
 */
import type { ReactNode } from 'react';

import { Activity, BarChart2, Info, RefreshCw } from 'react-feather';

import { FinancialNumber } from '../../../components/ui';
import type { ToolResultProps } from '../lib/component-registry';

interface NextEvent {
  kind?: string;
  months?: number;
  label?: string;
}

interface RepaymentShapeResult {
  available?: boolean;
  reason?: string;
  tier?: 'full' | 'estimated' | 'shape_only' | 'pending' | null;
  schedule_type?: string;
  type_label?: string | null;
  is_irregular?: boolean;
  frequency?: 'monthly' | 'quarterly' | 'semi_annual' | 'annual' | string;
  period_label?: string | null;
  hero_kind?: 'payment' | 'balloon' | 'irregular' | 'revolving' | 'none';
  period_payment?: number | null;
  coupon_payment?: number | null;
  tenor_months?: number | null;
  tenor_label?: string | null;
  grace_months?: number | null;
  principal?: number | null;
  is_revolving?: boolean;
  is_floating?: boolean;
  commitment_fee_pct?: number | null;
  has_balloon?: boolean;
  balloon_amount?: number | null;
  maturity_label?: string | null;
  maturity_exact?: boolean;
  payment_count?: number | null;
  first_payment_label?: string | null;
  next_event?: NextEvent | null;
  facility_count?: number | null;
}

const FREQUENCY_PAYMENT_LABEL: Record<string, string> = {
  monthly: 'Monthly payment',
  quarterly: 'Quarterly payment',
  semi_annual: 'Semi-annual payment',
  annual: 'Annual payment',
};

const BADGE_BY_KIND: Record<string, { label: string; tone: string }> = {
  payment: { label: 'Amortizing', tone: 'bg-teal/10 text-teal' },
  balloon: { label: 'Bullet', tone: 'bg-warning/10 text-warning' },
  irregular: { label: 'Sculpted', tone: 'bg-info/10 text-info' },
  revolving: { label: 'Revolving', tone: 'bg-orange/10 text-orange' },
  none: { label: 'Partial', tone: 'bg-surface-subtle text-txt-secondary' },
};

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function disclosureText(r: RepaymentShapeResult): string | null {
  if (r.is_revolving) {
    return 'Shape only. A revolving facility has no fixed repayment schedule; repayments depend on how much is drawn and when.';
  }
  if (r.is_irregular) {
    return 'Shape only. The term sheet describes a sculpted profile but does not itemise the individual instalments, so exact amounts and dates cannot be derived.';
  }
  if (r.tier === 'shape_only') {
    return 'Shape only. The interest rate was not extracted, so per-period amounts cannot be derived from the term sheet.';
  }
  if (r.tier === 'estimated') {
    return 'Estimated shape. The repayment type was not stated, so an equal-instalment schedule is assumed.';
  }
  if (r.has_balloon && r.is_floating) {
    return 'Estimated shape. Interest tracks a floating rate, so only the balloon is a fixed amount; per-period interest is not yet known.';
  }
  return null;
}

export function RepaymentShapeCard({ result }: ToolResultProps) {
  const r = (result ?? {}) as RepaymentShapeResult;

  // No card when the tool has nothing derivable — prose carries.
  if (r.available === false || !r.available) {
    return null;
  }

  const kind = r.hero_kind ?? 'none';
  const badge = BADGE_BY_KIND[kind] ?? BADGE_BY_KIND.none;
  const periodLabel = r.period_label ?? '';
  const disclosure = disclosureText(r);
  const showTimeline =
    (kind === 'payment' || kind === 'balloon') && isNum(r.tenor_months) && r.tenor_months > 0;

  return (
    <div className="animate-card-enter mt-2 border border-border bg-surface-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-orange">
            <BarChart2 size={16} />
          </span>
          <p className="text-caption font-semibold uppercase tracking-wide text-txt-secondary">
            Repayment shape
          </p>
        </div>
        <span
          className={`shrink-0 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${badge.tone}`}
        >
          {badge.label}
        </span>
      </div>

      {/* Hero — switches on hero_kind (engine outputs only) */}
      <Hero r={r} kind={kind} periodLabel={periodLabel} />

      {/* Facts 2x2 */}
      <Facts r={r} />

      {/* Timeline — signed → first repayment → maturity (payment/balloon only) */}
      {showTimeline && <Timeline r={r} />}

      {/* Degradation disclosure */}
      {disclosure && (
        <div className="flex items-start gap-2 border-t border-border bg-surface-subtle px-4 py-2.5">
          <span className="mt-px shrink-0 text-txt-tertiary">
            <Info size={14} />
          </span>
          <p className="text-[10.5px] leading-relaxed text-txt-tertiary">{disclosure}</p>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-border px-4 py-2">
        <span className="text-overline uppercase tracking-wide text-txt-tertiary">
          Source: uploaded term sheet
        </span>
      </div>
    </div>
  );
}

function Hero({
  r,
  kind,
  periodLabel,
}: {
  r: RepaymentShapeResult;
  kind: string;
  periodLabel: string;
}) {
  if (kind === 'payment' && isNum(r.period_payment)) {
    const overline = FREQUENCY_PAYMENT_LABEL[r.frequency ?? ''] ?? 'Scheduled payment';
    return (
      <div className="px-4 py-3">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">
          {overline}
        </span>
        <div className="mt-1.5 flex items-baseline gap-1">
          <FinancialNumber value={r.period_payment} format="currency" size="lg" />
          {periodLabel && (
            <span className="text-body-sm font-medium text-txt-tertiary">{periodLabel}</span>
          )}
        </div>
        <p className="mt-2 text-caption text-txt-tertiary">
          {r.type_label ?? 'Scheduled instalments'}
          {r.first_payment_label ? (
            <>
              {' · first payment '}
              <span className="font-mono text-txt-secondary">{r.first_payment_label}</span>
            </>
          ) : null}
        </p>
      </div>
    );
  }

  if (kind === 'balloon' && isNum(r.balloon_amount)) {
    return (
      <div className="px-4 py-3">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">
          Balloon at maturity
        </span>
        <div className="mt-1.5">
          <FinancialNumber value={r.balloon_amount} format="currency" size="lg" />
        </div>
        <p className="mt-2 text-caption text-txt-tertiary">
          Principal due{' '}
          {r.maturity_label ? (
            <span className="font-mono text-txt-secondary">{r.maturity_label}</span>
          ) : (
            'at maturity'
          )}
          {' · interest-only until then'}
          {isNum(r.coupon_payment) && r.coupon_payment > 0 ? (
            <>
              {' · interest ≈ '}
              <span className="font-mono text-txt-secondary">
                <FinancialNumber value={r.coupon_payment} format="currency" size="sm" />
              </span>
              {r.period_label ?? ''}
            </>
          ) : null}
        </p>
      </div>
    );
  }

  if (kind === 'revolving') {
    return (
      <StatementHero
        icon={<RefreshCw size={16} />}
        tone="text-orange"
        overline="Facility type"
        title="Revolving credit facility"
        sub="Draw and repay freely up to the limit; interest accrues only on the drawn balance."
      />
    );
  }

  if (kind === 'irregular') {
    return (
      <StatementHero
        icon={<Activity size={16} />}
        tone="text-info"
        overline="Repayment profile"
        title={r.type_label ?? 'Irregular schedule'}
        sub="The individual instalments are not itemised in the term sheet."
      />
    );
  }

  // hero_kind === 'none' with a renderable (shape_only) tier — disclosure carries.
  return (
    <StatementHero
      icon={<Info size={16} />}
      tone="text-txt-tertiary"
      overline="Repayment profile"
      title="Repayment schedule (partial)"
      sub="The full schedule cannot be derived from the extracted fields yet."
    />
  );
}

function StatementHero({
  icon,
  tone,
  overline,
  title,
  sub,
}: {
  icon: ReactNode;
  tone: string;
  overline: string;
  title: string;
  sub: string;
}) {
  return (
    <div className="px-4 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-txt-tertiary">
        {overline}
      </span>
      <div className="mt-1.5 flex items-center gap-2">
        <span className={`shrink-0 ${tone}`}>{icon}</span>
        <span className="text-body font-semibold text-txt-primary">{title}</span>
      </div>
      <p className="mt-2 text-caption text-txt-tertiary">{sub}</p>
    </div>
  );
}

function Facts({ r }: { r: RepaymentShapeResult }) {
  const facilityLabel = r.is_revolving ? 'Limit' : 'Facility';
  const repayment = r.is_revolving
    ? 'Revolving'
    : (r.type_label ?? (r.schedule_type ? capFirst(r.schedule_type.replace(/_/g, '-')) : '—'));
  const grace = isNum(r.grace_months) && r.grace_months > 0 ? `${fmtInt(r.grace_months)} mo` : 'None';
  const tenor = r.tenor_label ?? '—';

  return (
    <div className="grid grid-cols-2 border-t border-border">
      <Fact label={facilityLabel}>
        {isNum(r.principal) && r.principal > 0 ? (
          <FinancialNumber value={r.principal} format="currency" size="sm" />
        ) : (
          '—'
        )}
      </Fact>
      <Fact label="Repayment" divider>
        <span className="font-sans">{repayment}</span>
      </Fact>
      <Fact label="Grace">
        <span className="font-sans">{grace}</span>
      </Fact>
      <Fact label="Tenor" divider>
        <span className="font-sans">{tenor}</span>
      </Fact>
    </div>
  );
}

function Fact({
  label,
  children,
  divider,
}: {
  label: string;
  children: ReactNode;
  divider?: boolean;
}) {
  return (
    <div className={`px-4 py-2.5 ${divider ? 'border-l border-border' : ''}`}>
      <div className="text-[9px] font-bold uppercase tracking-wide text-txt-tertiary">{label}</div>
      <div className="mt-0.5 font-mono text-body-sm font-medium tabular-nums text-txt-primary">
        {children}
      </div>
    </div>
  );
}

function Timeline({ r }: { r: RepaymentShapeResult }) {
  const tenor = isNum(r.tenor_months) ? r.tenor_months : 0;
  const grace = isNum(r.grace_months) ? r.grace_months : 0;
  const firstRepayPct = tenor > 0 ? Math.max(0, Math.min(90, (grace / tenor) * 100)) : 0;

  return (
    <div className="border-t border-border px-4 py-3">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <span className="text-[9px] font-bold uppercase tracking-wide text-txt-tertiary">
          Signed → first repayment → maturity
        </span>
        {r.tenor_label && (
          <span className="font-mono text-[11px] tabular-nums text-txt-secondary">
            {r.tenor_label}
          </span>
        )}
      </div>
      <div className="relative h-1.5 w-full bg-surface-muted">
        <div className="absolute inset-y-0 left-0 bg-teal" style={{ width: `${firstRepayPct}%` }} />
      </div>
      <div className="mt-2 flex justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-[8.5px] font-bold uppercase tracking-wide text-txt-tertiary">
            {grace > 0 && r.first_payment_label ? 'First repayment' : 'Start'}
          </span>
          {grace > 0 && r.first_payment_label && (
            <span className="font-mono text-[11px] tabular-nums text-txt-secondary">
              {r.first_payment_label}
            </span>
          )}
        </div>
        <div className="flex flex-col items-end text-right">
          <span className="text-[8.5px] font-bold uppercase tracking-wide text-txt-tertiary">
            Maturity
          </span>
          {r.maturity_label && (
            <span className="font-mono text-[11px] tabular-nums text-txt-secondary">
              {r.maturity_label}
            </span>
          )}
        </div>
      </div>
      {grace > 0 && (
        <p className="mt-2.5 flex items-center gap-1.5 text-[9.5px] leading-snug text-txt-tertiary">
          <span className="inline-block h-[3px] w-3 shrink-0 bg-teal" />
          Teal = interest-only, before principal amortisation begins
        </p>
      )}
    </div>
  );
}

function capFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function fmtInt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n));
}
